
import { CITIES, FUNDING_TYPES } from '../config/cities';

/**
 * Generate all dynamic routes for the city-based structure
 */
export function generateCityRoutes() {
  const routes = [];

  // Generate city hub routes: /business-funding/[city]/
  for (const city of CITIES) {
    routes.push({
      path: `/business-funding/${city.slug}`,
      type: 'city-hub',
      city: city.slug
    });
  }

  // Generate city funding subpage routes: /business-funding/[city]/[funding-type]/
  for (const city of CITIES) {
    for (const fundingType of FUNDING_TYPES) {
      routes.push({
        path: `/business-funding/${city.slug}/${fundingType.slug}`,
        type: 'city-funding',
        city: city.slug,
        fundingType: fundingType.slug
      });
    }
  }

  // Generate city prequalification routes: /[city]-prequalification/
  for (const city of CITIES) {
    routes.push({
      path: `/${city.slug}-prequalification`,
      type: 'city-prequal',
      city: city.slug
    });
  }

  return routes;
}

/**
 * Get all city slugs for route generation
 */
export function getAllCitySlugs(): string[] {
  return CITIES.map(city => city.slug);
}

/**
 * Get all funding type slugs for route generation
 */
export function getAllFundingTypeSlugs(): string[] {
  return FUNDING_TYPES.map(type => type.slug);
}

/**
 * Get all city-funding combinations
 */
export function getAllCityFundingCombinations(): Array<{ city: string; fundingType: string }> {
  const combinations = [];
  for (const city of CITIES) {
    for (const fundingType of FUNDING_TYPES) {
      combinations.push({
        city: city.slug,
        fundingType: fundingType.slug
      });
    }
  }
  return combinations;
}

/**
 * Validate if a city slug exists
 */
export function isValidCitySlug(slug: string): boolean {
  return CITIES.some(city => city.slug === slug);
}

/**
 * Validate if a funding type slug exists
 */
export function isValidFundingTypeSlug(slug: string): boolean {
  return FUNDING_TYPES.some(type => type.slug === slug);
}

/**
 * Get breadcrumb data for navigation
 */
export function getBreadcrumbs(pathname: string) {
  const breadcrumbs = [{ label: 'Home', path: '/' }];

  if (pathname.includes('/business-funding')) {
    breadcrumbs.push({ label: 'Business Funding', path: '/business-funding' });

    const parts = pathname.split('/').filter(Boolean);
    if (parts.length >= 2) {
      const citySlug = parts[1];
      const city = CITIES.find(c => c.slug === citySlug);
      if (city) {
        breadcrumbs.push({ 
          label: city.name, 
          path: `/business-funding/${city.slug}` 
        });

        if (parts.length >= 3) {
          const fundingSlug = parts[2];
          const fundingType = FUNDING_TYPES.find(f => f.slug === fundingSlug);
          if (fundingType) {
            breadcrumbs.push({ 
              label: fundingType.name, 
              path: `/business-funding/${city.slug}/${fundingType.slug}` 
            });
          }
        }
      }
    }
  } else if (pathname.includes('-prequalification')) {
    const citySlug = pathname.replace('/', '').replace('-prequalification', '');
    const city = CITIES.find(c => c.slug === citySlug);
    if (city) {
      breadcrumbs.push({ 
        label: `${city.name} Pre-Qualification`, 
        path: `/${city.slug}-prequalification` 
      });
    }
  }

  return breadcrumbs;
}
